import { Card } from "@/components/ui/card";
import { Trophy, TrendingUp, Sparkles, AlertTriangle } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

function fmt(n) {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN")}`;
}

/**
 * Smart Summary Card — shows UpchaarQ's wins for the period plus rule-based
 * insight strings (Hindi + English).
 */
export default function SmartSummaryCard({ data }) {
  const { lang } = useI18n();
  const wins = data?.wins || {};
  const insights = data?.insights || [];
  const netPositive = (wins.net_saving || 0) >= 0;

  return (
    <Card className="medical-card p-6 bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-emerald-200"
      data-testid="analytics-smart-summary">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-emerald-500 flex items-center justify-center shrink-0 shadow-lg shadow-emerald-500/30">
          <Trophy className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-semibold text-slate-900">
              {lang === "hi" ? "UpchaarQ ne is period mein kiya:" : "UpchaarQ delivered this period:"}
            </h3>
            <Sparkles className="w-4 h-4 text-amber-500" />
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="text-sm text-slate-700 flex items-center gap-2">
              <span className="text-emerald-600">•</span>
              <span data-testid="ss-recovered">
                {wins.recovered_no_shows || 0} no-shows recovered → <b>{fmt(wins.recovered_amount)}</b> saved
              </span>
            </div>
            <div className="text-sm text-slate-700 flex items-center gap-2">
              <span className="text-emerald-600">•</span>
              <span data-testid="ss-followups">
                {wins.followups_converted || 0} follow-ups reactivated → <b>{fmt(wins.followup_revenue)}</b> earned
              </span>
            </div>
            <div className="text-sm text-slate-700 flex items-center gap-2">
              <span className="text-slate-400">•</span>
              <span>Platform cost: <b>{fmt(wins.platform_cost)}</b></span>
            </div>
            <div className={`text-sm flex items-center gap-2 ${netPositive ? "text-emerald-800" : "text-rose-700"}`}>
              <span>{netPositive ? "🎯" : "⚠️"}</span>
              <span data-testid="ss-net-saving">
                <b>NET {netPositive ? "SAVING" : "COST"}: {fmt(Math.abs(wins.net_saving || 0))}</b>
              </span>
            </div>
          </div>

          {insights.length > 0 && (
            <div className="mt-5 pt-4 border-t border-emerald-200/70">
              <p className="text-[11px] uppercase tracking-[0.16em] text-emerald-800 font-semibold mb-2 flex items-center gap-1.5">
                <TrendingUp className="w-3.5 h-3.5" /> Smart Insights
              </p>
              <ul className="space-y-2">
                {insights.map((ins, i) => (
                  <li key={i} className="text-sm text-slate-700 flex items-start gap-2" data-testid={`ss-insight-${i}`}>
                    {ins.type === "return_rate_high" ? (
                      <span>🎉</span>
                    ) : ins.type === "high_no_show" ? (
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                    ) : (
                      <Sparkles className="w-4 h-4 text-sky-500 mt-0.5 shrink-0" />
                    )}
                    <span>{lang === "hi" ? ins.hi : ins.en}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
